import React, { useState, useEffect, useCallback } from 'react';
import { History, Calendar, TrendingUp, Users, BarChart3 } from 'lucide-react';
import api from 'services/api';
import { DateRangeFilterBlur } from 'components/filters';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const HistoricoEscala = () => {
  const [loading, setLoading] = useState(true);
  const [historico, setHistorico] = useState([]);
  const [estatisticas, setEstatisticas] = useState({});
  const today = new Date();
  const lastMonth = new Date(today);
  lastMonth.setMonth(today.getMonth() - 1);
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [range, setRange] = useState({ 
    presetId: 'custom', 
    start: toISO(lastMonth), 
    end: toISO(today) 
  });

  const carregarHistorico = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/operacional/historico-escala', {
        params: {
          data_inicio: range.start,
          data_fim: range.end
        }
      });

      if (response.data?.success) {
        setHistorico(response.data.data.historico || response.data.data || []);
        setEstatisticas(response.data.data.estatisticas || response.data.estatisticas || {});
      } else {
        // Fallback para estrutura alternativa
        setHistorico(response.data?.data || []);
        setEstatisticas(response.data?.estatisticas || {});
      }
    } catch (error) {
      console.error('❌ Erro ao carregar histórico:', error);
      setHistorico([]);
      setEstatisticas({});
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end]);

  useEffect(() => {
    carregarHistorico();
  }, [carregarHistorico]);

  // Preparar dados para gráficos
  const dadosGrafico = historico.length > 0 && historico[0].data 
    ? historico 
    : historico.map((item, index) => ({
        data: item.data || item.data_agendamento || `Dia ${index + 1}`,
        agendamentos: item.total_agendamentos || item.agendamentos || 0,
        concluidos: item.total_concluidos || item.concluidos || 0
      }));

  const dadosPorTecnico = estatisticas.por_tecnico || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center">
            <History className="h-5 w-5 mr-2 text-[color:var(--text-muted)]" />
            Histórico de Escala
          </h3>
          <p className="text-sm text-[color:var(--text-muted)]">Análise histórica das escalas dos técnicos</p>
        </div>
        <DateRangeFilterBlur
          value={range}
          onChange={(newRange) => {
            setRange(newRange);
          }}
        />
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[color:var(--surface)] p-4 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[color:var(--accent)]">Total de Agendamentos</p>
              <p className="text-2xl font-bold text-[color:var(--accent)]">{estatisticas.total_agendamentos || historico.length || 0}</p>
            </div>
            <Calendar className="h-8 w-8 text-[color:var(--accent)]" />
          </div>
        </div>
        <div className="bg-[color:var(--surface)] p-4 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[color:var(--status-good)]">Taxa de Conclusão</p>
              <p className="text-2xl font-bold text-[color:var(--status-good)]">{estatisticas.taxa_conclusao || 0}%</p>
            </div>
            <TrendingUp className="h-8 w-8 text-[color:var(--status-good)]" />
          </div>
        </div>
        <div className="bg-[color:var(--surface)] p-4 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[color:var(--accent)]">Técnicos Ativos</p>
              <p className="text-2xl font-bold text-[color:var(--accent)]">{estatisticas.tecnicos_ativos || 0}</p>
            </div>
            <Users className="h-8 w-8 text-[color:var(--accent)]" />
          </div>
        </div>
        <div className="bg-[color:var(--surface)] p-4 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[color:var(--status-warn)]">Média Diária</p>
              <p className="text-2xl font-bold text-[color:var(--status-warn)]">{estatisticas.media_diaria || 0}</p>
            </div>
            <BarChart3 className="h-8 w-8 text-[color:var(--status-warn)]" />
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)] shadow-sm">
          <h4 className="text-lg font-semibold mb-4 text-[color:var(--text-primary)]">Evolução de Agendamentos</h4>
          {dadosGrafico.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dadosGrafico}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="data" tick={{ fill: 'var(--text-muted)' }} />
                <YAxis tick={{ fill: 'var(--text-muted)' }} />
                <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} labelStyle={{ color: 'var(--text-primary)' }} />
                <Line type="monotone" dataKey="agendamentos" stroke="var(--accent)" strokeWidth={2} name="Agendamentos" />
                <Line type="monotone" dataKey="concluidos" stroke="var(--status-good)" strokeWidth={2} name="Concluídos" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-[color:var(--text-muted)]">
              Nenhum dado disponível para o período selecionado
            </div>
          )}
        </div>

        <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)] shadow-sm">
          <h4 className="text-lg font-semibold mb-4 text-[color:var(--text-primary)]">Distribuição por Técnico</h4>
          {dadosPorTecnico.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dadosPorTecnico}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="tecnico" tick={{ fill: 'var(--text-muted)' }} />
                <YAxis tick={{ fill: 'var(--text-muted)' }} />
                <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} labelStyle={{ color: 'var(--text-primary)' }} />
                <Bar dataKey="agendamentos" fill="var(--accent)" name="Agendamentos" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-[color:var(--text-muted)]">
              Nenhum dado disponível para o período selecionado
            </div>
          )}
        </div>
      </div>

      {/* Tabela de Histórico */}
      <div className="bg-[color:var(--surface)] rounded-lg border border-[color:var(--border)] shadow-sm">
        <div className="p-4 border-b border-[color:var(--border)]">
          <h4 className="text-lg font-semibold text-[color:var(--text-primary)]">Detalhamento do Histórico</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[color:var(--border)]">
            <thead className="bg-[color:var(--surface-muted)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase">Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase">Técnico</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase">OS</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase">Tempo</th>
              </tr>
            </thead>
            <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-[color:var(--border)] border-t-[color:var(--accent)]"></div>
                      <span className="ml-2 text-[color:var(--text-muted)]">Carregando histórico...</span>
                    </div>
                  </td>
                </tr>
              ) : historico.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-[color:var(--text-muted)]">
                    Nenhum histórico encontrado para o período selecionado
                  </td>
                </tr>
              ) : (
                historico.map((item, index) => (
                  <tr key={index} className="hover:bg-[color:var(--surface-muted)]">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[color:var(--text-primary)]">
                      {item.data ? new Date(item.data).toLocaleDateString('pt-BR') : 
                       item.data_agendamento ? new Date(item.data_agendamento).toLocaleDateString('pt-BR') : 
                       'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[color:var(--text-primary)]">
                      {item.tecnico || item.tecnico_nome || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[color:var(--text-primary)]">
                      {item.os_id || item.os || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        item.status === 'Concluído' ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-good)]' :
                        item.status === 'Cancelado' ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-bad)]' :
                        'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]'
                      }`}>
                        {item.status || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[color:var(--text-primary)]">
                      {item.tempo_execucao || item.tempo || 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HistoricoEscala;
